import React from "react";
import { Play, Pause, X, Volume2, VolumeX } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

const Equalizer = () => (
  <div className="flex items-end h-4" aria-hidden>
    <span className="eq-bar" />
    <span className="eq-bar" />
    <span className="eq-bar" />
    <span className="eq-bar" />
  </div>
);

const MiniPlayer = () => {
  const {
    current,
    playing,
    toggle,
    stop,
    volume,
    setVolume,
    muted,
    toggleMute,
    nowPlaying,
  } = usePlayer();

  if (!current) return null;

  const isVideo = current.kind === "video";

  return (
    <div
      data-testid="mini-player"
      className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/90 border-t border-gold/25"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
        {/* Thumbnail (audio) or TV indicator */}
        <div className="relative w-14 h-14 md:w-16 md:h-16 shrink-0 rounded overflow-hidden border border-gold/30 bg-black">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${nowPlaying?.art || current.image})` }}
          />
          <div className="absolute inset-0 bg-black/30" />
          {isVideo && (
            <div className="absolute inset-0 grid place-items-center">
              <span className="font-condensed uppercase tracking-widest text-[9px] text-crimson-live">● Live TV</span>
            </div>
          )}
        </div>

        {/* Now playing */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-condensed uppercase tracking-widest text-[10px] text-crimson-live">● Live</span>
            <span className="font-condensed uppercase tracking-widest text-[10px] text-gold truncate">
              {current.name}
            </span>
            {typeof nowPlaying?.listeners === "number" && (
              <span className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500">
                · {nowPlaying.listeners} listening
              </span>
            )}
          </div>
          <div className="font-body text-sm text-white truncate" data-testid="now-playing-title">
            {nowPlaying?.title || current.tagline}
            {nowPlaying?.artist ? (
              <span className="text-neutral-400"> — {nowPlaying.artist}</span>
            ) : null}
          </div>
        </div>

        {/* Equalizer */}
        <div className="hidden sm:block">{playing ? <Equalizer /> : null}</div>

        {/* Volume */}
        <div className="hidden md:flex items-center gap-2">
          <button
            data-testid="mute-btn"
            onClick={toggleMute}
            className="text-neutral-300 hover:text-gold transition-colors"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            data-testid="volume-slider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-24 accent-[#D4AF37]"
          />
        </div>

        {/* Controls */}
        <button
          data-testid="mini-toggle"
          onClick={toggle}
          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gold hover:bg-gold-light text-black transition-colors"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>

        <button
          data-testid="mini-close"
          onClick={stop}
          className="text-neutral-400 hover:text-crimson transition-colors"
          aria-label="Stop"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default MiniPlayer;
